import { DataTable, usePagination } from '@/components/table/DataTable';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { useConsultantWorkspacesService } from '@/services/consultant';
import type { ConsultantWorkspaceItem } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
} from '@tanstack/react-table';
import { ExternalLink, Search } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useConsultantContext } from './ConsultantLayout';

export const ConsultantClientOrganisationsPage: FC = () => {
  const { orgId } = useConsultantContext();
  const { workspaces, isLoading } = useConsultantWorkspacesService(orgId);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) return workspaces;
    const q = searchQuery.toLowerCase();
    return workspaces.filter(w => w.name.toLowerCase().includes(q));
  }, [workspaces, searchQuery]);

  const columns: ColumnDef<ConsultantWorkspaceItem>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Workspace name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <span className="text-sm text-neutral-900">{row.original.name}</span>
        ),
      },
      {
        id: 'link',
        header: 'Link',
        size: 120,
        cell: ({ row }) => (
          <a
            href={`/workspace/${row.original.uname}`}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="size-3.5" />
            Open
          </a>
        ),
      },
    ],
    []
  );

  const { pagination } = usePagination({
    initialSize: 50,
    manualPagination: false,
  });

  return (
    <FlexCol gap="6" w="full" p="6" className="max-w-[1600px]">
      <div>
        <h2 className="text-xl font-semibold">Client organisations</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Workspaces managed by your organisation
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <Input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search workspaces..."
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredWorkspaces}
        isLoading={isLoading}
        pagination={pagination}
        rowCount={filteredWorkspaces.length}
        options={{
          getPaginationRowModel: getPaginationRowModel(),
          getSortedRowModel: getSortedRowModel(),
        }}
      />
    </FlexCol>
  );
};
